import {
  flexRender,
  getCoreRowModel,
  RowData,
  useReactTable,
} from '@tanstack/react-table';
import Loading from 'components/Loading';
import { Icon } from 'components/icon';
import { Message } from 'modules';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { IndeterminateCheckbox } from './Components';
import { itemsPerPageOptions } from './config';
import type { TableListDataProps } from './types';
import { useColumnsDefine, usePagination } from './utils';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'configs';
import { useTableRowFocus } from '../useTableRowFocus';

const TableListDataEdit = <TData extends RowData>(
  props: TableListDataProps<TData>,
) => {
  const {
    columns: columnDefined,
    data,
    filterKey,
    callBackData,
    ...tProps
  } = props;

  const [rowSelection, setRowSelection] = useState({});
  const [editingRowId, setEditingRowId] = useState<string | null>(null);

  const { pagination, setPagination } = usePagination({
    initPageIndex: DEFAULT_PAGE_INDEX,
    initPageSize: DEFAULT_PAGE_SIZE,
  });

  // Lấy dữ liệu theo trang hiện tại (pagination)
  const memoData: any = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];
    return arr;
  }, [data]);

  const getRowFocusProps = useTableRowFocus(memoData);

  // const memoData: any = useMemo(() => data ?? [], [data, pagination]);

  const { columns, ...rest } = useColumnsDefine({
    columns: columnDefined,
    ...tProps,
  });

  const memoColumns = useMemo(() => columns, [columns]);

  const table = useReactTable({
    data: memoData,
    columns: memoColumns,
    state: {
      rowSelection,
      pagination,
    },
    pageCount: Math.ceil(memoData?.length / pagination.pageSize),
    enableRowSelection: tProps?.canSelectable
      ? (row) => Boolean(tProps?.canSelectable?.(row.original))
      : rest?.selectable,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    debugTable: tProps?.debugTable,
  });

  const handleChange = useCallback(() => {
    const selectedRows = table
      .getSelectedRowModel()
      .flatRows.map((row) => row.original);

    tProps.onSelectedRowsCallback?.(selectedRows);
  }, [table.getSelectedRowModel()]);

  useEffect(() => {
    handleChange();
  }, [handleChange]);

  const renderCellContent = useCallback((cell: any) => {
    const renderedContent = flexRender(
      cell.column.columnDef.cell,
      cell.getContext(),
    );
    const rawValue = cell.getValue?.();

    const isTextValue =
      (typeof rawValue === 'string' || typeof rawValue === 'number') &&
      String(rawValue).trim() !== '';

    if (!isTextValue) {
      return renderedContent;
    }

    const text = String(rawValue);

    return (
      <span className='Table__CellText' title={text}>
        {renderedContent}
      </span>
    );
  }, []);

  const getColumnSizeStyle = useCallback((columnDef: any) => {
    const columnSize = columnDef?.meta?.columnSize;

    if (!columnSize) return undefined;

    const style: React.CSSProperties = {
      boxSizing: 'border-box',
      width: columnSize.width,
      minWidth: columnSize.minWidth,
      maxWidth: columnSize.maxWidth,
    };

    return style;
  }, []);

  return (
    <Loading isLoading={false}>
      <div className='Table'>
        <div
          className='Header row justify-content-end align-items-center g10'
          style={{ position: 'sticky', top: '0', left: '-10px', zIndex: 1 }}
        >
          {typeof tProps?.customHeader === 'function' ? (
            tProps?.customHeader(memoData)
          ) : (
            <></>
          )}
        </div>

        <table>
          <thead className='Table__Head'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      style={getColumnSizeStyle(header.column.columnDef)}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody className='Table__Body'>
            {typeof tProps?.customTopRow === 'function' ? (
              tProps?.customTopRow(memoData)
            ) : (
              <></>
            )}

            {memoData.length ? (
              table
                .getRowModel()
                .rows.slice(
                  table.getState().pagination.pageIndex *
                    table.getState().pagination.pageSize,
                  (table.getState().pagination.pageIndex + 1) *
                    table.getState().pagination.pageSize,
                )
                .map((row) => {
                  return (
                    <tr key={row.id} {...getRowFocusProps(row.original)}>
                      {row.getVisibleCells().map((cell, index) => {
                        const isEditing =
                          editingRowId === `${row.id}-${cell.column.id}`;
                        // Only allow editing for certain columns if needed
                        const isEditable = (cell.column.columnDef.meta as any)
                          ?.editable;

                        // Handler to enable editing mode for this cell
                        const handleCellClick = (e: React.MouseEvent) => {
                          e.stopPropagation();
                          setEditingRowId(`${row.id}-${cell.column.id}`);
                        };

                        // Handler to save and exit editing mode
                        const handleInputBlur = (
                          e?:
                            | React.FocusEvent<HTMLInputElement>
                            | React.KeyboardEvent<HTMLInputElement>
                            | React.FocusEvent<HTMLSelectElement>
                            | React.ChangeEvent<HTMLSelectElement>,
                        ) => {
                          setEditingRowId(null);
                          if (tProps.setData && e && e.target) {
                            const newValue = (e.target as HTMLInputElement)
                              .value;
                            tProps.setData((prevData: any[]) =>
                              prevData.map((item: any, idx: number) =>
                                idx === row.index
                                  ? { ...item, [cell.column.id]: newValue }
                                  : item,
                              ),
                            );
                          }
                        };

                        const cellValue = cell.getValue();

                        return (
                          <td
                            key={index}
                            onClick={handleCellClick}
                            style={{
                              ...getColumnSizeStyle(cell.column.columnDef),
                              cursor: isEditable ? 'pointer' : undefined,
                            }}
                          >
                            {isEditing && isEditable ? (
                              (cell.column.columnDef.meta as any)?.options ? (
                                <select
                                  defaultValue={cellValue as string}
                                  onBlur={(
                                    e: React.FocusEvent<HTMLSelectElement>,
                                  ) => {
                                    setEditingRowId(null);
                                    handleInputBlur(e);
                                  }}
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    width: '100%',
                                  }}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLSelectElement>,
                                  ) => {
                                    handleInputBlur(e);
                                  }}
                                >
                                  {(
                                    cell.column.columnDef.meta as any
                                  )?.options.map((option: any) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type='text'
                                  defaultValue={cellValue as string}
                                  onBlur={handleInputBlur}
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    width: '100%',
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleInputBlur(e);
                                    }
                                  }}
                                />
                              )
                            ) : (
                              renderCellContent(cell)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
            ) : (
              <tr>
                <td colSpan={20}>
                  <Message type='emptyData' message='Không tìm thấy dữ liệu' />
                </td>
              </tr>
            )}
            {typeof tProps?.customBottomRow === 'function' ? (
              tProps?.customBottomRow(memoData)
            ) : (
              <></>
            )}
          </tbody>

          {memoData.length && rest?.selectable ? (
            <tfoot>
              <tr>
                <td>
                  <IndeterminateCheckbox
                    {...{
                      checked: table.getIsAllPageRowsSelected(),
                      indeterminate: table.getIsSomePageRowsSelected(),
                      onChange: table.getToggleAllPageRowsSelectedHandler(),
                    }}
                  />
                </td>
                <td colSpan={20}>
                  Tổng: ({data?.length || table.getRowModel().rows.length})
                </td>
              </tr>
            </tfoot>
          ) : (
            <></>
          )}
        </table>

        {data && data.length ? (
          <div
            className='Footer row pl0 pr0'
            style={{ position: 'sticky', bottom: '0', left: '0', zIndex: 1 }}
          >
            <div className='PaginationInfo col-6'>
              {rest?.selectable ? (
                <>
                  {`${Object.keys(rowSelection).length} / ${
                    data?.length || table.getPreFilteredRowModel().rows.length
                  } đã chọn`}

                  {typeof tProps?.customSelectedFooter === 'function' ? (
                    tProps?.customSelectedFooter(memoData)
                  ) : (
                    <></>
                  )}
                </>
              ) : (
                `Tổng: ${
                  data?.length || table.getPreFilteredRowModel().rows.length
                }`
              )}
            </div>

            <div
              className='col-6'
              {...{
                style: {
                  textAlign: 'right',
                },
              }}
            >
              <div className='Pagination'>
                <button
                  type='button'
                  className='btnPagination prev'
                  onClick={() => {
                    table.previousPage();
                    // setFetchData((prev) => prev + 1)
                  }}
                  disabled={!table.getCanPreviousPage()}
                >
                  <Icon.Prev />
                </button>

                <strong>
                  {!isNaN(table.getPageCount())
                    ? `${
                        table.getState().pagination.pageIndex + 1
                      } / ${table.getPageCount()}`
                    : table.getState().pagination.pageIndex + 1}
                </strong>

                <button
                  type='button'
                  className='btnPagination next'
                  onClick={() => {
                    table.nextPage();
                    // setFetchData((prev) => prev + 1)
                  }}
                  disabled={!table.getCanNextPage()}
                >
                  <Icon.Next />
                </button>
              </div>

              <div className='Pagination'>
                <select
                  title='Page size'
                  className='pageSize'
                  name='pageSize'
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => {
                    table.setPageSize(Number(e.target.value));
                    // setFetchData((prev) => prev + 1)
                  }}
                >
                  {itemsPerPageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {typeof tProps?.customFooter === 'function' ? (
              tProps?.customFooter(memoData)
            ) : (
              <></>
            )}
          </div>
        ) : (
          <></>
        )}
      </div>
    </Loading>
  );
};

export default TableListDataEdit;
